import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  ModalHeader, 
  ModalBody, 
  ModalFooter, 
  Button, 
  Input, 
  Label, 
  FormGroup,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane
} from 'reactstrap';
import MDEditor from '@uiw/react-md-editor';
import LeagueTeams from './LeagueTeams';
import LeagueWeeks from './LeagueWeeks';
import LeagueSchedules from './LeagueSchedules';
import LeagueAdmins from './LeagueAdmins';
import LeagueTeeChoices from './LeagueTeeChoices';
import api from '../../services/api';

const EditLeagueModal = ({ isOpen, toggle, onSave, league, services }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [teamCount, setTeamCount] = useState(0);
  const [editedLeague, setEditedLeague] = useState({
    title: '',
    description: '',
    service_id: '',
    state: 'New',
    sort_order: 99,
    lock_resources: false,
    show_leaderboard: false,
    payment_link: '',
    payment_link_split: ''
  });

  useEffect(() => {
    if (league) {
      setEditedLeague({
        title: league.title || '',
        description: league.description || '',
        service_id: league.service?.id || '',
        state: league.state || 'New',
        sort_order: league.sort_order || 99,
        lock_resources: Boolean(league.lock_resources),
        show_leaderboard: Boolean(league.show_leaderboard),
        payment_link: league.payment_link || '',
        payment_link_split: league.payment_link_split || ''
      });
    }
  }, [league]);

  useEffect(() => {
    // Reset to details tab when modal opens
    if (isOpen) {
      setActiveTab('details');
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchTeamCount = async () => {
      if (league?.id) {
        try {
          const response = await api.get(`/league-teams/?league=${league.id}`);
          setTeamCount(response.data.length);
        } catch (error) {
          console.error('Error fetching team count:', error);
          setTeamCount(0);
        }
      } else {
        setTeamCount(0);
      }
    };
    fetchTeamCount();
  }, [league?.id, isOpen]);

  const handleTeamCountChange = (count) => {
    setTeamCount(count);
  };

  const handleSave = () => {
    onSave(editedLeague);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditedLeague(prev => ({
      ...prev,
      [name]: type === 'checkbox'
        ? checked
        : name === 'sort_order'
          ? parseInt(value) || 99
          : value
    }));
  };

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl">
      <ModalHeader toggle={toggle}>Edit League: {league?.title}</ModalHeader>
      <ModalBody>
        <Nav tabs>
          <NavItem>
            <NavLink
              className={activeTab === 'details' ? 'active' : ''}
              onClick={() => toggleTab('details')}
              style={{ cursor: 'pointer' }}
            >
              Details
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={activeTab === 'admins' ? 'active' : ''}
              onClick={() => toggleTab('admins')}
              style={{ cursor: 'pointer' }}
            >
              Admins
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={activeTab === 'weeks' ? 'active' : ''}
              onClick={() => toggleTab('weeks')}
              style={{ cursor: 'pointer' }}
            >
              Weeks
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={activeTab === 'schedules' ? 'active' : ''}
              onClick={() => toggleTab('schedules')}
              style={{ cursor: 'pointer' }}
            >
              Schedules
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={activeTab === 'teams' ? 'active' : ''}
              onClick={() => toggleTab('teams')}
              style={{ cursor: 'pointer' }}
            >
              Teams ({teamCount})
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={activeTab === 'tees' ? 'active' : ''}
              onClick={() => toggleTab('tees')}
              style={{ cursor: 'pointer' }}
            >
              Tee Choices
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab} className="mt-3">
          <TabPane tabId="details">
            <FormGroup>
              <Label for="title">Title *</Label>
              <Input
                type="text"
                id="title"
                name="title"
                value={editedLeague.title}
                onChange={handleChange}
                placeholder="League title"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="description">Description</Label>
              <MDEditor
                value={editedLeague.description}
                onChange={(value) => setEditedLeague(prev => ({ ...prev, description: value || '' }))}
                preview="edit"
                height={300}
              />
            </FormGroup>
            <FormGroup>
              <Label for="service_id">Service *</Label>
              <Input
                type="select"
                id="service_id"
                name="service_id"
                value={editedLeague.service_id}
                onChange={handleChange}
                required
              >
                <option value="">-- Select Service --</option>
                {services.map((svc) => (
                  <option key={svc.id} value={svc.id}>
                    {svc.title} ({svc.duration} min)
                  </option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup check className="mb-3">
              <Input
                type="checkbox"
                id="lock_resources"
                name="lock_resources"
                checked={editedLeague.lock_resources}
                onChange={handleChange}
              />
              <Label check for="lock_resources">
                Lock resources for this league
              </Label>
            </FormGroup>
            <FormGroup check className="mb-3">
              <Input
                type="checkbox"
                id="show_leaderboard"
                name="show_leaderboard"
                checked={editedLeague.show_leaderboard}
                onChange={handleChange}
              />
              <Label check for="show_leaderboard">
                Show leaderboard for this league
              </Label>
            </FormGroup>
            <FormGroup>
              <Label for="state">State</Label>
              <Input
                type="select"
                id="state"
                name="state"
                value={editedLeague.state}
                onChange={handleChange}
              >
                <option value="New">New</option>
                <option value="Registration">Registration</option>
                <option value="Active">Active</option>
                <option value="Archived">Archived</option>
              </Input>
            </FormGroup>
            <FormGroup>
              <Label for="sort_order">Sort Order</Label>
              <Input
                type="number"
                id="sort_order"
                name="sort_order"
                value={editedLeague.sort_order}
                onChange={handleChange}
                placeholder="99"
              />
            </FormGroup>
            <FormGroup>
              <Label for="payment_link">Payment Link (Full)</Label>
              <Input
                type="url"
                id="payment_link"
                name="payment_link"
                value={editedLeague.payment_link}
                onChange={handleChange}
                placeholder="https://..."
              />
              <small className="text-muted">Optional URL for full team payment</small>
            </FormGroup>
            <FormGroup>
              <Label for="payment_link_split">Payment Link (Split)</Label>
              <Input
                type="url"
                id="payment_link_split"
                name="payment_link_split"
                value={editedLeague.payment_link_split}
                onChange={handleChange}
                placeholder="https://..."
              />
              <small className="text-muted">Optional URL for split per-player payments</small>
            </FormGroup>
          </TabPane>

          <TabPane tabId="teams">
            {league && <LeagueTeams league={league} onTeamCountChange={handleTeamCountChange} />}
          </TabPane>

          <TabPane tabId="weeks">
            {league && <LeagueWeeks league={league} />}
          </TabPane>

          <TabPane tabId="schedules">
            {league && <LeagueSchedules league={league} />}
          </TabPane>

          <TabPane tabId="admins">
            {league && <LeagueAdmins league={league} />}
          </TabPane>

          <TabPane tabId="tees">
            {league && <LeagueTeeChoices league={league} />}
          </TabPane>
        </TabContent>
      </ModalBody>
      <ModalFooter>
        {activeTab === 'details' && (
          <>
            <Button color="primary" onClick={handleSave} disabled={!editedLeague.title || !editedLeague.service_id}>
              Save Changes
            </Button>{' '}
          </>
        )}
        <Button color="secondary" onClick={toggle}>
          {activeTab === 'details' ? 'Cancel' : 'Close'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default EditLeagueModal;
